import api from "../../services/api.service";
import { ActionTree } from "vuex";
import {
	MapLayersState,
	MapItemTypeData,
	MapItemData,
	MapLayerData,
	MapLayerBounds,
	MapLayer,
	MapRequest
} from "./types";
import { RootState } from "../types";
import { async } from "rxjs/internal/scheduler/async";
import MapLayerItemTypes from "@/constants/MapLayerItemTypes";
import { Devices, MapLayerRequest } from "@/store/map-setup/types";
import { AxiosResponse } from "axios";
import { getNewCancelTokenSource } from "@/utils/cancellableQueryHelper";

export const actions: ActionTree<MapLayersState, RootState> = {
	async loadMapLayers({ commit }, data): Promise<AxiosResponse<MapLayer>> {
		// @refactor do not return data here, just put it to store & get via getters
		const mapLayers = await api.loadMapLayers(data);
		commit("setMapLayers", mapLayers.data);
		return mapLayers;
	},

	async loadMapLayersWithCancelToken({ state, commit }, data: MapRequest): Promise<AxiosResponse<MapLayer>> {
		const cancelTokenSource = getNewCancelTokenSource(state.cancelTokenSource, true);
		commit("setCancelTokenSource", cancelTokenSource);

		const mapLayers = await api.loadMapLayers(data, state.cancelTokenSource);
		commit("setMapLayers", mapLayers.data);
		return mapLayers;
	},

	async loadMapLayersBounds({ commit }) {
		const bounds = await api.loadMapLayersBounds();
		commit("mapLayersBounds", bounds.data);
	},

	/**
	 * Loads the map layer bounds for a specified group, and applies them to the store. Also supports loading layers
	 * for a group in a remote region.
	 * @param param0 Vuex store commit.
	 * @param group The id of the group, or an object containing a group and region id.
	 * @returns The map layer bounds.
	 */
	async loadAreaMapLayersBounds(
		{ commit },
		group: number | { groupId: number; regionId: number }
	): Promise<MapLayerBounds> {
		let bounds = null;

		if (typeof group == "object") {
			bounds = (await api.loadAreaMapLayersBounds(group.groupId, group.regionId)).data;
		} else {
			bounds = (await api.loadAreaMapLayersBounds(group)).data;
		}

		commit("mapLayersBounds", bounds);
		return bounds;
	},

	async loadMapLayersItemTypes({ commit }) {
		const { data } = await api.loadMapLayersItemTypes();
		const mapItemTypes = data as MapItemTypeData[];
		mapItemTypes.forEach(mapItemType => {
			mapItemType.visible = !mapItemType.hideByDefault;
		});

		commit("setMapLayersItemTypes", mapItemTypes);
	},

	/**
	 * Loads the map layers, including any images, for a specified group id. Also supports loading map layers for
	 * groups in remote regions.
	 * @param param0 Vuex store commit.
	 * @param group The id of the group to load map layers for, or an object containing a group and region id.
	 * @returns An array of map layers for the group.
	 */
	async loadMapLayersByGroupID(
		{ commit },
		group: number | { regionId: number; groupId: number }
	): Promise<MapLayerData[]> {
		let mapLayers = null;

		if (typeof group == "object") {
			mapLayers = (await api.loadMapLayersByGroupID(group.groupId, group.regionId)).data;
		} else {
			mapLayers = (await api.loadMapLayersByGroupID(group)).data;
		}

		commit("setMapLayersForGroup", mapLayers);
		return mapLayers;
	},

	async loadMapLayerItemsByMapLayer(_, mapLayerId: number): Promise<MapItemData[]> {
		let { data } = await api.loadMapLayerItemsByMapLayer(mapLayerId);
		// Filter out Guards - they should be added on the Dispatch Setup page
		data = data.filter(f => f.mapLayerItemTypeID !== MapLayerItemTypes.Guard);
		return data;
	},

	async loadMapUnlinkedItemsByGroup(_, groupId: number): Promise<MapItemData[]> {
		let { data } = await api.loadMapUnlinkedItemsByGroup(groupId);

		return data;
	},

	async loadPlottedMapLayerItemsByGroupID(_, groupID: Number): Promise<MapItemData[]> {
		let data = await api.loadPlottedMapLayerItemsByGroupID(groupID);
		return data;
	},

	async searchAssets({ commit }, searchQuery): Promise<any> {
		return await api.searchAssets(searchQuery);
	},

	async searchAssetsByDistance({ commit }, searchQuery): Promise<any> {
		return await api.searchAssetsByDistance(searchQuery);
	}
};
